import { posthogHeaders } from './analytics/client';
import { CLIENT_TIMEOUT_GRACE_MS, requestBudgetMs } from '../core/timeouts';
import type { Course } from '../core/course';
import type { Overrun } from '../core/schedule';
import type { CollabMaterialListing } from '../core/collab';
import type { Calendar, DayItem, IsoDate, ItemMaterial, PlanRecord } from '../core/types';
export type GetToken = () => Promise<string | null>;
export class ApiError extends Error {
  constructor(public status: number, public reason: string, message: string, public nextAvailableAt?: string) { super(message); }
}
/** Any failure as an `ApiError`; anything that is not one is a request that never finished. */
export const toApiError = (failure: unknown): ApiError => failure instanceof ApiError ? failure : new ApiError(0, 'unreachable', 'We could not finish that request. Try again.');
/** An inconclusive subscription lookup is retryable, not a revocation. */
export const accessRevoked = (reason: string) => reason === 'disabled' || reason === 'subscription_required';
export async function call<T>(getToken: GetToken, path: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  options.signal?.addEventListener('abort', cancel, { once: true });
  if (options.signal?.aborted) cancel();
  const timeoutMs = requestBudgetMs(options.method, path) + CLIENT_TIMEOUT_GRACE_MS;
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new ApiError(504, 'request_timeout', 'This request took too long. Please try again.'));
      controller.abort();
    }, timeoutMs);
  });
  try {
    return await Promise.race([timeout, (async () => {
      const token = await getToken();
      // Token acquisition cannot be aborted, but a late token must not start another request.
      controller.signal.throwIfAborted();
      const response = await fetch(path, { ...options, signal: controller.signal, headers: { ...posthogHeaders(), ...(typeof options.body === 'string' ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers } });
      const body = (response.status === 204 ? {} : await response.json()) as { error?: string; reason?: string; message?: string; nextAvailableAt?: string };
      if (!response.ok) throw new ApiError(response.status, body.reason ?? body.error ?? 'unavailable', body.message ?? 'We could not finish that request.', body.nextAvailableAt);
      return body as T;
    })()]);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, 'unreachable', 'We could not connect. Check your connection and try again.');
  } finally {
    clearTimeout(timer!);
    options.signal?.removeEventListener('abort', cancel);
  }
}
export function safeUrl(value: string): string | undefined {
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined; } catch { return undefined; }
}

/** Every plan route answers with the plan, its overrun, and the date the Worker used (KTD8). */
export interface PlanResponse { plan: PlanRecord; overrun: Overrun; today: IsoDate }
export interface PlanSummary { id: string; course: Course; calendar: Calendar; built: boolean; lastOpenedAt: string | null; createdAt: string; updatedAt: string }
/** A calendar save also names each other plan it was copied to, and how far that one now runs over (R3, KTD1). */
export interface CalendarCopy { planId: string; weeksOver: number }
export interface CalendarSaveResponse extends PlanResponse { copies: CalendarCopy[] }
export interface PlanListResponse { plans: PlanSummary[]; limit: number }
/**
 * My week's data (KTD2): every built plan as `GET /api/plans/:id` returns it, the unbuilt ones by course, both by creation order.
 * Each plan carries `plan.spanOverrides` and the `items` of `weekOf(today)` (KTD3).
 */
export interface WeekResponse { plans: PlanRecord[]; unbuilt: { id: string; course: Course }[]; today: IsoDate }
/**
 * `GET /api/me`. `myWeek`, `dayItems`, and `collabAttachments` are kill switches read the other
 * way round; all fail open (KTD4, KTD6, KTD7).
 */
export interface Me { userId: string; email: string | null; subscriptionState: string; fixtures: boolean; myWeek?: boolean; dayItems?: boolean; collabAttachments?: boolean }
/**
 * What `POST /api/plans/:id/items` takes; `PUT …/items/:itemId` takes the same without `date`.
 * With `collabAttachments` on, `materialIds` (at most 10, in order) replaces the attachments and
 * is left as stored when absent; `link` is refused unless it is null (clears a stored link) or
 * the stored link itself (R1, R2, KTD2). With it off, `link` works as before and `materialIds`
 * is ignored.
 */
export interface DayItemInput { title: string; link?: string | null; standardIds?: string[]; materialIds?: string[] }
/** `POST` and `PUT` on an item answer with it. */
export interface DayItemResponse { item: DayItem }
/** `PUT /api/plans/:id/items/order` takes `{ date, ids }` and answers with that day's items in their new order. */
export interface DayItemsOrderResponse { items: DayItem[] }
/** `GET /api/items?from=&to=` (KTD1): each built plan's items in the range, by plan id; at most 42 days. */
export interface ItemsRangeResponse { items: Record<string, DayItem[]> }
export type { CollabMaterialListing, ItemMaterial };
/** `GET /api/collab/materials?q=` (R3): the teacher's materials across their Collab groups, newest first. 503 `collab_unavailable` when Collab cannot answer. */
export interface CollabMaterialsResponse { materials: CollabMaterialListing[] }
/**
 * `POST /api/plans/:id/collab-uploads` (multipart `title`, `file`; R5): the new material, to
 * send in the item's `materialIds`. A refusal carries Collab's `error` and `reason` (R7).
 */
export interface CollabUploadResponse { material: ItemMaterial }
export const collabMaterialsPath = (q: string) => `/api/collab/materials?${new URLSearchParams({ q })}`;
export const collabUploadPath = (planId: string) => `${planPath(planId)}/collab-uploads`;

/**
 * What the item form asks of Collab, through the Planner (R3, R5, KTD1): passed in, so the form
 * and the picker can be tested with a fake. A failure is an `ApiError` whose `reason` is
 * Collab's own (R7) or `collab_unavailable` (R10).
 */
export interface CollabAccess {
  search(q: string, signal?: AbortSignal): Promise<CollabMaterialListing[]>;
  upload(file: File, title: string): Promise<ItemMaterial>;
}

/** The live `CollabAccess` for one plan. The browser sets the multipart boundary and length itself. */
export const collabAccess = (getToken: GetToken, planId: string): CollabAccess => ({
  search: async (q, signal) => (await call<CollabMaterialsResponse>(getToken, collabMaterialsPath(q), { signal })).materials,
  upload: async (file, title) => {
    const form = new FormData();
    form.append('title', title);
    form.append('file', file);
    return (await call<CollabUploadResponse>(getToken, collabUploadPath(planId), { method: 'POST', body: form })).material;
  },
});
export const planPath = (id: string) => `/api/plans/${encodeURIComponent(id)}`;
export const itemsPath = (planId: string) => `${planPath(planId)}/items`;
export const itemPath = (planId: string, itemId: string) => `${itemsPath(planId)}/${encodeURIComponent(itemId)}`;

/** The teacher's own calendar date, sent with every request that depends on it (KTD8). */
export function localToday(now = new Date()): IsoDate {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** "Grade 4 Mathematics, Texas" — how a plan is named everywhere it is listed. */
export const courseLabel = (course: Course) => `${course.grade === 'K' ? 'Kindergarten' : `Grade ${course.grade}`} ${course.subject}, ${course.state}`;
